import React, {
  useEffect,
  useState,
  FunctionComponent,
} from 'react';

import renderModal from '@components/show_page/modals/renderModal';
import PrimaryInput from '@components/library/inputs/PrimaryInput';
import { InputType } from '@components/library/inputs/Input.props';
import { BFLoader } from '@components/common/loader/main';
import {
  CloseButton,
  DangerButton,
} from '@components/library/button/index';
import { getBrandguide, deleteBrandguide } from '@api/v4/resources/brandguides';


interface DeleteModalProps {
  brandguideKey: string;
  closeModal: () => void;
}

interface Attributes {
  name: string;
  slug: string;
}

const defaultData: Attributes = { name: '', slug: '' };

const DeleteModal: FunctionComponent<DeleteModalProps> = ({
  brandguideKey,
  closeModal,
}) => {
  const [initialData, setInitialData] = useState(defaultData);
  const [slug, setSlug] = useState<string>();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleDelete = async (): Promise<void> => {
    setIsSubmitting(true);

    try {
      await deleteBrandguide(brandguideKey);
      Notify.create({
        title: bfTranslate('Brandguide deleted! Refreshing...'),
        type: "success"
      });
      setIsSubmitDisabled(true); // maintain disabled button while page reloads
      window.location.reload();
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchBrandguide = async (): Promise<void> => {
    try {
      const { attributes } = await getBrandguide(brandguideKey);
      setInitialData({ name: attributes.name, slug: attributes.slug });
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandguide();
    return closeModal;
  }, []);

  useEffect(() => {
    setIsSubmitDisabled(slug !== initialData.slug);
  }, [slug]);

  return (
    <div className="modal-content-wrapper brandguide-modal">
      <div className="modal-content-wrapper__header">
        <span className="bff-trash icon" />
        <h3>{bfTranslate("Delete Brandguide")}</h3>
        <CloseButton onClick={closeModal} />
      </div>
      {isLoading ? (
        <div className="loader-container"><BFLoader /></div>
      ) : (
        <div className="modal-content-wrapper__body delete-modal">
          <h1 className="warning-title">{bfTranslate('Are you sure you want to delete')} {initialData.name}?</h1>
          <p className="warning-copy">{bfTranslate('There is no way to undo this action. It is immediate, and all users belonging to this Brandguide will lose access.')}</p>
          <p className="warning-copy">{bfTranslate('To confirm deletion, please enter the URL for the Brandguide below:')} <span className="slug">{initialData.slug}</span></p>
          <PrimaryInput
            attributes={{
              name: "slug",
              onChange: (e: InputChangeEvent): void => setSlug(e.target.value),
              placeholder: initialData.slug,
              type: InputType.Text,
            }}
            input={{ value: slug }}
          />
          <div className="button-container">
            <DangerButton
              disabled={isSubmitDisabled}
              isLoading={isSubmitting}
              onClick={handleDelete}
              size="medium"
            >
              {bfTranslate('Delete')}
            </DangerButton>
          </div>
        </div>
      )}
    </div>
  );
};

const ModalComponent = renderModal(DeleteModal, 'brandguide-settings-modal');
export default ModalComponent;
